import { common } from '@libs/domain';
import { characterCode } from '@libs/enums';
import {
  type FieldErrorField,
  FieldSet,
  type FieldSetProps,
  FileField,
  Item,
  kebabCase,
  SelectBoxField,
  useValidator,
} from '@ps-smart-cp/ppfw-client';
import { forwardRef, type ReactNode, type Ref, useImperativeHandle } from 'react';
import type { ImportFileField } from '../models';

interface ImportFieldProps<T extends ImportFileField = ImportFileField> {
  legendProps: FieldSetProps['legendProps'];
  importFileObj: T;
  setImportFileObj: (importFileObj: T) => void;
  errorFields?: string[];
  fileIdPrefix?: string;
  required?: boolean;
  disabled?: boolean;
  children?: ReactNode;
}

interface ImportFieldRef {
  validate: () => FieldErrorField[];
}

function InnerComponent(props: ImportFieldProps, ref: Ref<ImportFieldRef>) {
  const {
    disabled,
    legendProps,
    importFileObj,
    setImportFileObj,
    errorFields,
    children,
    fileIdPrefix,
    required,
  } = props;

  const itemConfig = {
    file: Item.of(
      common.names.file,
      common.attributes.file.replaceRequired(required ?? common.attributes.file.required),
    ),
    characterCode: Item.of(
      common.names.characterCode,
      common.attributes.characterCode.replaceRequired(required ?? common.attributes.characterCode.required),
    ),
  };

  const validator = useValidator();
  const validate = () => {
    return validator
      .with(importFileObj, itemConfig)
      .execute()
      .map((s) => (fileIdPrefix ? { ...s, field: kebabCase(`${fileIdPrefix}-${s.field}`) } : s));
  };

  useImperativeHandle(ref, () => ({ validate }));

  return (
    <FieldSet legendProps={legendProps}>
      <FileField
        id={fileIdPrefix !== undefined ? `${fileIdPrefix}-file` : undefined}
        obj={importFileObj}
        setObj={setImportFileObj}
        prop="file"
        items={itemConfig}
        errorFields={errorFields}
        disabled={disabled}
      />
      <SelectBoxField
        id={fileIdPrefix !== undefined ? `${fileIdPrefix}-character-code` : undefined}
        obj={importFileObj}
        setObj={setImportFileObj}
        prop="characterCode"
        items={itemConfig}
        enumType={characterCode.enum}
        enumNameConfig={characterCode.nameConfig}
        needEmptyOption
        errorFields={errorFields}
        disabled={disabled}
      />
      {children}
    </FieldSet>
  );
}

export const ImportField = forwardRef(InnerComponent);
export type { ImportFieldProps, ImportFieldRef };
